/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { IScrollablePaneStyleProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { flatten } from 'lodash';
import uniqBy from 'lodash.uniqby';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TargetFile } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';

export type TranslationLocalFileTableProps = {
    selectedLocalFiles: File[];
    onSelectedLocalFilesChange: (files: File[]) => void;
    isLoading: boolean;
};

const messages = defineMessages({
    fileNameHeader: {
        id: 'translation.translationLocalFileTable.fileNameHeader',
        defaultMessage: 'Name'
    },
    fileSizeHeader: {
        id: 'translation.translationLocalFileTable.fileSizeHeader',
        defaultMessage: 'File size (KB)'
    },
    addFilesHeader: {
        id: 'translation.translationLocalFileTable.addFilesHeader',
        defaultMessage: 'Add file(s)'
    },
    removeFileHeader: {
        id: 'translation.translationLocalFileTable.removeFileHeader',
        defaultMessage: 'Remove file'
    },
    localStorageTablePlaceholder: {
        id: 'translation.translationLocalFileTable.localStorageTablePlaceholder',
        defaultMessage: 'Select Add files to get started'
    },
    addFilesSuccess: {
        id: 'translation.translationLocalFileTable.addFilesSuccess',
        defaultMessage: 'Added files successfully'
    },
    removeFilesSuccess: {
        id: 'translation.translationLocalFileTable.removeFilesSuccess',
        defaultMessage: 'Removed files successfully'
    }
});

const theme = getScopedTheme('TranslationLocalFileTable');

const listContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { position: 'relative', flex: 1 } });
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { flex: 1, border: `1px solid ${theme.borderColor}`, minHeight: '250px', overflow: 'auto' }
});
const scrollPaneStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    contentContainer: { overflow: 'inherit' }
});

export const TranslationLocalFileTable = (props: TranslationLocalFileTableProps) => {
    const { selectedLocalFiles, onSelectedLocalFilesChange, isLoading } = props;

    const { localize } = useLocalization();
    const { selection, selectedItems } = useSelection<TargetFile>();
    const { announceAction, announcedComponent } = useAnnounced();

    const inputRef = React.useRef<HTMLInputElement>(null);

    const onHandleLocalFiles = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const { files: filesList } = event.target as HTMLInputElement;
        const files = [...Array.from(filesList)];
        const uniqueSelectedLocalFiles = uniqBy(flatten([files, selectedLocalFiles]), 'name');

        onSelectedLocalFilesChange(uniqueSelectedLocalFiles);
        announceAction(localize(messages.addFilesSuccess));
    };

    const onRemoveSelectedLocalFiles = () => {
        const filteredUploadedFiles = selectedLocalFiles.filter(f => selectedItems.every(removedFile => removedFile.name !== f.name));
        onSelectedLocalFilesChange(filteredUploadedFiles);
        selection.setAllSelected(false);
        announceAction(localize(messages.removeFilesSuccess));
        logDTButtonClick('document translation', 'jobCreation', 'uploadFiles', 'Button:removeFile', 'filesSelection');
    };

    const onHandleAddClick = () => {
        inputRef.current.click();
        logDTButtonClick('document translation', 'jobCreation', 'uploadFiles', 'Button:addFile', 'filesSelection');
    };

    const renderFilesList = () => (
        <Stack styles={listContainerStyles}>
            <ScrollablePane styles={scrollPaneStyles}>
                <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                    <Table<File>
                        items={selectedLocalFiles}
                        selection={selection}
                        columns={[
                            {
                                key: 'fileName',
                                fieldName: 'name',
                                name: localize(messages.fileNameHeader),
                                minWidth: 200,
                                onRender: (item: File) => (
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20, maxWidth: '100%' }}>
                                        <Icon iconName="TextDocument" />
                                        <LuisText styles={{ root: { overflow: 'hidden' } }}>{item.name}</LuisText>
                                    </Stack>
                                )
                            },
                            {
                                key: 'fileSize',
                                fieldName: 'size',
                                name: localize(messages.fileSizeHeader),
                                minWidth: 120,
                                onRender: (item: File) => <LuisText>{Math.floor(item.size / 1000)}</LuisText>
                            }
                        ]}
                        emptyPlaceholder={localize(messages.localStorageTablePlaceholder)}
                    />
                </FocusZone>
            </ScrollablePane>
        </Stack>
    );

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'addFiles',
            onClick: onHandleAddClick,
            iconProps: { iconName: 'add' },
            text: localize(messages.addFilesHeader),
            disabled: isLoading,
            ariaLabel: localize(messages.addFilesHeader)
        },
        {
            key: 'removeFile',
            onClick: onRemoveSelectedLocalFiles,
            iconProps: { iconName: 'delete' },
            text: localize(messages.removeFileHeader),
            disabled: !selectedItems.length || !selection,
            ariaLabel: localize(messages.removeFileHeader)
        }
    ];

    return (
        <Stack verticalFill>
            <Stack grow>
                <LuisCommandBar items={commandBarItems} />
                <Stack grow styles={containerStyles}>
                    {renderFilesList()}
                </Stack>
            </Stack>
            <input
                multiple
                type="file"
                ref={inputRef}
                style={{ display: 'none' }}
                accept={LuisConstants.TranslationTool.SupportedFileFormats}
                onChange={onHandleLocalFiles}
            />
            {announcedComponent}
        </Stack>
    );
};
